import {
  COMPANY_SIZE_OPTIONS,
  INDUSTRY_OPTIONS,
  JOB_TITLE_OPTIONS,
  REFERRAL_SOURCE_OPTIONS,
  SELECT_INPUT_OTHER_VALUE,
  SENIORITY_OPTIONS
} from "@/src/common/business-layer/constants"
import { SelectOption, SimpleSelect } from "@/src/common/components/Select"
import { AnalyticsProfileUpdateProps } from "@/src/common/types"
import { useAnalytics } from "@/src/sb/components/Analytics"
import { TextInput } from "@/src/sb/components/Input"
import AuthForm from "@/src/sb/components/auth/AuthForm"
import { CitySelect, DialingCodeSelect } from "@/src/sb/components/auth/fields"
import {
  ProfileUpdate,
  ProfileUpdateSchema
} from "@/src/sb/core/validatorSchemas/profile-update"
import { SBAPIInstance } from "@/src/sb/data-layer"
import { useUser } from "@/src/sb/hooks/useUser"
import { AxiosError } from "axios"
import classNames from "classnames"
import { FormikErrors, useFormik } from "formik"
import { useSession } from "next-auth/react"
import { ReactNode, useEffect, useState } from "react"

import Button from "../../Button"

const LabelTextStyle = ({
  children,
  boldLabel
}: {
  children: ReactNode
  boldLabel?: boolean
}) => <div className={boldLabel ? "font-bold" : "font-light"}>{children}</div>

const constructOtherValue = (
  value: string | undefined | null,
  fallbackValue: string
): string => (value ? `${SELECT_INPUT_OTHER_VALUE} - ${value}` : fallbackValue)

type ProfileUpdateFormProps = {
  boldInputLabel?: boolean
  onSuccess?: () => void
  onFailure?: () => void
  type: AnalyticsProfileUpdateProps["type"]
  theme?: "dark" | "light"
}

const ProfileUpdateForm = ({
  boldInputLabel = false,
  onSuccess,
  onFailure,
  type,
  theme = "dark"
}: ProfileUpdateFormProps) => {
  const { data: session } = useSession()
  const { userType } = useUser()
  const token = (session?.user as any)?.accessToken
  const analytics = useAnalytics()
  const [dialingCode, setDialingCode] = useState("")

  useEffect(() => {
    analytics?.trackProfileRequest({
      member_status: userType,
      type
    })
  }, [])

  const handleProfileUpdate = async (values: ProfileUpdate) => {
    const profileData: ProfileUpdate = {
      ...values,
      seniority: values.seniority ?? "None",
      phoneNumber:
        values.phoneNumber && dialingCode
          ? `${dialingCode}${values.phoneNumber}`
          : "",
      industry: constructOtherValue(values.industryAlt, values.industry),
      jobTitle: constructOtherValue(values.jobTitleAlt, values.jobTitle),
      referralSource: constructOtherValue(
        values.referralSourceAlt,
        values.referralSource
      )
    }

    // dispose alt fields
    for (const prop in profileData) {
      if (prop.endsWith("Alt")) delete profileData[prop as keyof ProfileUpdate]
    }

    try {
      await SBAPIInstance.updateMemberProfile(profileData, token)
      analytics.trackOnboardingFormSubmission({
        fields_entered: Object.keys(profileData).join(",")
      })

      analytics.trackProfileUpdate({
        member_status: userType,
        state: "success",
        type
      })
      onSuccess?.()
    } catch (e: unknown) {
      onFailure?.()
      const trackingProps: AnalyticsProfileUpdateProps = {
        member_status: userType,
        state: "failure",
        type
      }
      const errors: Record<string, Array<string>> = ((e as AxiosError)?.response
        ?.data ?? {}) as Record<string, Array<string>>

      form.setErrors(
        Object.keys(errors).reduce(
          (_errors, _error) => ({ ..._errors, [_error]: errors[_error][0] }),
          {}
        ) as FormikErrors<ProfileUpdate>
      )
      analytics.trackProfileUpdate(trackingProps)
    }
  }

  const form = useFormik<ProfileUpdate>({
    initialValues: {
      phoneNumber: "",
      companyName: "",
      companySize: "",
      city: "",
      country: "",
      jobTitle: "",
      jobTitleAlt: "",
      seniority: "",
      seniorityAlt: "",
      industry: "",
      industryAlt: "",
      referralSource: "",
      referralSourceAlt: ""
    },
    enableReinitialize: true,
    validationSchema: ProfileUpdateSchema,
    onSubmit: handleProfileUpdate
  })

  const getReactSelectValue = (
    value?: string | null,
    options?: Array<Record<string, any>>
  ) =>
    value
      ? {
          value: value ?? "",
          label:
            options?.find((option) => option.value === value)?.label ??
            value ??
            ""
        }
      : ""

  const CountryCodeSelect = (
    <DialingCodeSelect
      value={dialingCode}
      onChange={setDialingCode}
      hasError={Boolean(form.touched.phoneNumber && form.errors.phoneNumber)}
    />
  )

  return (
    <section className={"w-full font-jakarta md:min-w-[448px] text-left"}>
      <header className="text-center mb-5">
        <h1 className="dark:text-stears_white text-xl font-bold mb-2">
          Finish sign up
        </h1>
        <p
          className={classNames(
            "max-w-[200px] mx-auto dark:text-stears_white text-xs",
            [boldInputLabel ? "font-bold" : "font-light"]
          )}
        >
          {type === "new_user"
            ? "Complete your sign up by providing the following information."
            : "Please provide the following information to enable us to serve you better"}
        </p>
      </header>

      <main>
        <form onSubmit={form.handleSubmit}>
          <section>
            <AuthForm.FormItem
              labelText={
                <LabelTextStyle boldLabel={boldInputLabel}>
                  Phone
                </LabelTextStyle>
              }
              labelHTMLFor="phoneNumber"
            >
              <TextInput
                name="phoneNumber"
                type="text"
                value={form.values.phoneNumber ?? ""}
                leftReactNode={CountryCodeSelect}
                extraInputClasses={["!indent-3.5 w-full"]}
                errorShiftCorrected={false}
                errors={
                  form.touched.phoneNumber && form.errors.phoneNumber
                    ? [form.errors.phoneNumber]
                    : []
                }
                onChange={form.handleChange}
                onBlur={form.handleBlur}
              />
            </AuthForm.FormItem>

            <AuthForm.FormItem
              labelText="Company Name"
              labelHTMLFor="companyName"
            >
              <TextInput
                name="companyName"
                type="text"
                value={form.values.companyName ?? ""}
                onChange={form.handleChange}
                extraInputClasses={["w-full"]}
                errorShiftCorrected={false}
                onBlur={form.handleBlur}
                errors={
                  form.touched.companyName && form.errors.companyName
                    ? [form.errors.companyName]
                    : []
                }
              />
            </AuthForm.FormItem>

            <AuthForm.FormItem labelText="Job Title" labelHTMLFor="jobTitle">
              <SimpleSelect
                name="jobTitle"
                isSearchable={false}
                value={getReactSelectValue(form.values.jobTitle)}
                onChange={(selectedOption: SelectOption) => {
                  form.setFieldValue("jobTitle", selectedOption.value)
                }}
                options={JOB_TITLE_OPTIONS}
                onBlur={form.handleBlur}
                errors={
                  form.touched.jobTitle && form.errors.jobTitle
                    ? [form.errors.jobTitle]
                    : []
                }
              />

              {form.values.jobTitle === SELECT_INPUT_OTHER_VALUE ? (
                <section className={"mt-2"}>
                  <TextInput
                    name="jobTitleAlt"
                    placeholder={"Type in your Job title"}
                    type="text"
                    extraInputClasses={["w-full"]}
                    value={form.values.jobTitleAlt ?? ""}
                    onChange={form.handleChange}
                    errorShiftCorrected={false}
                    onBlur={form.handleBlur}
                    errors={
                      form.touched.jobTitleAlt && form.errors.jobTitleAlt
                        ? [form.errors.jobTitleAlt]
                        : []
                    }
                  />
                </section>
              ) : null}
            </AuthForm.FormItem>

            <AuthForm.FormItem
              labelText={
                <LabelTextStyle boldLabel={boldInputLabel}>
                  Seniority
                </LabelTextStyle>
              }
              labelHTMLFor="seniority"
            >
              <SimpleSelect
                name="seniority"
                isSearchable={false}
                value={getReactSelectValue(form.values.seniority)}
                onChange={(selectedOption: SelectOption) => {
                  form.setFieldValue(
                    "seniority",
                    (selectedOption as SelectOption).value
                  )
                }}
                options={SENIORITY_OPTIONS}
                onBlur={form.handleBlur}
                errors={
                  form.touched.seniority && form.errors.seniority
                    ? [form.errors.seniority]
                    : []
                }
              />

              {form.values.seniority === SELECT_INPUT_OTHER_VALUE ? (
                <section className={"mt-2"}>
                  <TextInput
                    name="seniorityAlt"
                    placeholder={"Type in your Seniority Level"}
                    type="text"
                    extraInputClasses={["w-full"]}
                    value={form.values.seniorityAlt ?? ""}
                    onChange={form.handleChange}
                    errorShiftCorrected={false}
                    onBlur={form.handleBlur}
                    errors={
                      form.touched.seniorityAlt && form.errors.seniorityAlt
                        ? [form.errors.seniorityAlt]
                        : []
                    }
                  />
                </section>
              ) : null}
            </AuthForm.FormItem>

            <AuthForm.FormItem
              labelText={
                <LabelTextStyle boldLabel={boldInputLabel}>
                  Industry
                </LabelTextStyle>
              }
              labelHTMLFor="industry"
            >
              <SimpleSelect
                name="industry"
                isSearchable={false}
                placeholder={"Select..."}
                value={getReactSelectValue(form.values.industry)}
                onChange={(selectedOption: SelectOption) =>
                  form.setFieldValue("industry", selectedOption.value)
                }
                options={INDUSTRY_OPTIONS}
                onBlur={form.handleBlur}
                errors={
                  form.touched.industry && form.errors.industry
                    ? [form.errors.industry]
                    : []
                }
              />

              {form.values.industry === SELECT_INPUT_OTHER_VALUE ? (
                <section className={"mt-2"}>
                  <TextInput
                    name="industryAlt"
                    placeholder={"Type in your Industry"}
                    type="text"
                    extraInputClasses={["w-full"]}
                    value={form.values.industryAlt ?? ""}
                    onChange={form.handleChange}
                    errorShiftCorrected={false}
                    onBlur={form.handleBlur}
                    errors={
                      form.touched.industryAlt && form.errors.industryAlt
                        ? [form.errors.industryAlt]
                        : []
                    }
                  />
                </section>
              ) : null}
            </AuthForm.FormItem>

            <AuthForm.FormItem
              labelText={
                <LabelTextStyle boldLabel={boldInputLabel}>
                  Number of Employees
                </LabelTextStyle>
              }
              labelHTMLFor="companySize"
            >
              <SimpleSelect
                name="companySize"
                isSearchable={false}
                placeholder={"Select..."}
                value={getReactSelectValue(form.values.companySize)}
                onChange={(selectedOption: SelectOption) =>
                  form.setFieldValue("companySize", selectedOption.value)
                }
                options={COMPANY_SIZE_OPTIONS}
                onBlur={form.handleBlur}
                errors={
                  form.touched.companySize && form.errors.companySize
                    ? [form.errors.companySize]
                    : []
                }
              />
            </AuthForm.FormItem>

            <AuthForm.FormItem
              labelText={
                <LabelTextStyle boldLabel={boldInputLabel}>
                  <div className={"flex items-center gap-4 w-full"}>
                    <section className={"grow"}>City</section>
                    {form.values.city ? (
                      <section className={"text-right"}>
                        <button
                          type={"button"}
                          className={"text-white"}
                          onClick={() => {
                            form.setFieldValue("city", "")
                            form.setFieldValue("country", "")
                          }}
                        >
                          Clear city
                        </button>
                      </section>
                    ) : null}
                  </div>
                </LabelTextStyle>
              }
              labelHTMLFor="city"
            >
              <CitySelect
                value={form.values.city}
                onChange={({ city, country }) => {
                  form.setFieldValue("city", city)
                  form.setFieldValue("country", country)
                }}
                onBlur={form.handleBlur}
                errors={
                  form.touched.city && form.errors.city
                    ? [form.errors.city]
                    : []
                }
              />
            </AuthForm.FormItem>

            {form.values.city && (
              <AuthForm.FormItem labelText="Country" labelHTMLFor="country">
                <TextInput
                  name="country"
                  type="text"
                  extraInputClasses={["w-full"]}
                  value={form.values.country ?? ""}
                  readOnly
                  disabled
                  errorShiftCorrected={false}
                  onBlur={form.handleBlur}
                  errors={
                    form.touched.country && form.errors.country
                      ? [form.errors.country]
                      : []
                  }
                />
              </AuthForm.FormItem>
            )}

            <AuthForm.FormItem
              labelText={
                <LabelTextStyle boldLabel={boldInputLabel}>
                  How did you hear about us?
                </LabelTextStyle>
              }
              labelHTMLFor="referralSource"
            >
              <SimpleSelect
                name="referralSource"
                placeholder={"Select..."}
                isSearchable={false}
                value={getReactSelectValue(form.values.referralSource)}
                onChange={(selectedOption: SelectOption) =>
                  form.setFieldValue(
                    "referralSource",
                    (selectedOption as SelectOption).value
                  )
                }
                options={REFERRAL_SOURCE_OPTIONS}
                onBlur={form.handleBlur}
                errors={
                  form.touched.referralSource && form.errors.referralSource
                    ? [form.errors.referralSource]
                    : []
                }
              />

              {form.values.referralSource === SELECT_INPUT_OTHER_VALUE ? (
                <section className={"mt-2"}>
                  <TextInput
                    name="referralSourceAlt"
                    placeholder={"Type in how you heard about us"}
                    type="text"
                    extraInputClasses={["w-full"]}
                    value={form.values.referralSourceAlt ?? ""}
                    onChange={form.handleChange}
                    errorShiftCorrected={false}
                    onBlur={form.handleBlur}
                    errors={
                      form.touched.referralSourceAlt &&
                      form.errors.referralSourceAlt
                        ? [form.errors.referralSourceAlt]
                        : []
                    }
                  />
                </section>
              ) : null}
            </AuthForm.FormItem>
          </section>

          <section className={"mb-24"}>
            <Button
              type={"primary"}
              roundness={"edge"}
              stretch
              htmlType={"submit"}
              disabled={!form.isValid || !form.dirty || form.isSubmitting}
              extraClassNames={
                theme == "dark" ? "dark:bg-[#4B5563] dark:h-[40px]" : ""
              }
            >
              Finish Sign Up
            </Button>
          </section>
        </form>
      </main>
    </section>
  )
}

export default ProfileUpdateForm
